import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { AGE_GATES, CONSENT_VERSIONS, DISCLAIMERS } from '@erectus/shared';
import type { UserConsent } from '@erectus/shared';
import { Button, Card, ErrorState, Field, TextInput, Toggle } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { canCreateAccount, canOptInToDataSharing, useAuth } from '@/stores/auth.js';
import { saveConsent, saveProfile } from '@/lib/repo.js';
import { setProps, track } from '@/lib/analytics.js';
import { SharingBenefits, SharingTerms } from '@/features/data/SharingBenefits.js';
import { sendSessionsOnPhone } from '@/features/data/sendSessionsOnPhone.js';

/**
 * Sign-up consent flow (docs/LEGAL_PRIVACY.md §3):
 *   birth year (13+ gate) → 18+ only: detection-improvement sharing, yes or no
 *   (asked first, right under the birth year) → ToS + Privacy (check 1)
 *   → consumer health data (check 2)
 *
 * The checks are deliberately separate: MHMDA requires collection consent to be
 * distinct from the general terms, and sharing is distinct from both. Sharing is
 * asked first so nobody has to find it in Settings.
 *
 * "Yes, share" starts selected (product decision 2026-10-17); "No thanks" is one
 * tap, the same size, right next to it, and sharing can be turned off any time.
 * Open legal question, LEGAL §3: regulators read a pre-selected opt-in as weaker
 * consent (MHMDA "clear affirmative act", CCPA dark-pattern rules, GDPR Recital
 * 32). Review with counsel before launch.
 *
 * No pain, symptom or condition question appears anywhere in this flow (LEGAL §0).
 */
export function ConsentPage(): ReactNode {
  const { user, refreshUserDoc } = useAuth();
  const navigate = useNavigate();
  const [birthYear, setBirthYear] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptHealthData, setAcceptHealthData] = useState(false);
  const [share, setShare] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const year = Number(birthYear);
  const yearValid = /^\d{4}$/.test(birthYear) && year >= 1900 && year <= new Date().getFullYear();
  const tooYoung = yearValid && !canCreateAccount(year);
  const askShare = yearValid && canOptInToDataSharing(year);
  const ready = yearValid && !tooYoung && acceptTerms && acceptHealthData;

  const submit = async (): Promise<void> => {
    if (!user || !ready) return;
    setBusy(true);
    setError(null);
    const now = Date.now();
    const optIn = askShare && share;
    const consent: UserConsent = {
      tosVersion: CONSENT_VERSIONS.tos,
      privacyVersion: CONSENT_VERSIONS.privacy,
      chdpVersion: CONSENT_VERSIONS.chdp,
      acceptedAt: now,
      dataOptIn: optIn,
      dataOptInAt: optIn ? now : null,
      dataOptInVersion: optIn ? CONSENT_VERSIONS.dataOptIn : null,
      dataOptInSignature: null,
      dataWithdrawnAt: null,
    };
    try {
      await saveConsent(user.uid, consent);
      // Birth year is part of the profile; the rest is collected in onboarding.
      await saveProfile(user.uid, {
        displayName: user.displayName ?? null,
        birthYear: year,
        createdAt: Date.now(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        goals: [],
        sittingHoursBucket: '6-8',
        equipment: { laptop: true, monitor: false, chairType: 'office' },
      });
      track('onboarding_step', { step: 1, completed: true });
      if (askShare) {
        track('data_optin', { value: optIn });
        setProps({ has_data_optin: optIn ? 'true' : 'false' });
      }
      // A re-consent (new terms) can come with sessions already on this phone.
      if (optIn) void sendSessionsOnPhone(user.uid);
      await refreshUserDoc();
      navigate('/onboarding', { replace: true });
    } catch {
      setError('Could not save your choices. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Before we start" subtitle="Your birth year and a few choices." />

      {error ? <ErrorState title="Not saved" message={error} /> : null}

      <Card className="space-y-4">
        <Field
          label="Birth year"
          hint={`Erectus is for ages ${AGE_GATES.minimumAccountAge} and up.`}
        >
          <TextInput
            inputMode="numeric"
            autoComplete="bday-year"
            placeholder="1998"
            maxLength={4}
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        {tooYoung ? (
          <p className="text-sm font-medium text-[color:var(--color-state-slouch)]">
            Sorry — you need to be at least {AGE_GATES.minimumAccountAge} to use Erectus.
          </p>
        ) : null}
      </Card>

      {askShare ? (
        <Card className="space-y-3">
          <div>
            <p className="font-semibold">Make Erectus more accurate for you</p>
            <p className="mt-1 text-sm text-ink-soft">
              Erectus gets better at reading posture from real sessions. Share the joint coordinates from
              yours — numbers, never video or pictures — and the moments you mark as wrong. Shared files
              carry a scrambled ID, not your name or email.
            </p>
          </div>
          <SharingBenefits />
          <SharingTerms />
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Share keypoint data">
            {([true, false] as const).map((value) => (
              <button
                key={String(value)}
                type="button"
                role="radio"
                aria-checked={share === value}
                onClick={() => setShare(value)}
                className={`min-h-11 rounded-xl border px-3 py-2.5 text-sm font-semibold ${
                  share === value ? 'border-ink bg-ink text-white' : 'border-line bg-surface text-ink'
                }`}
              >
                {value ? 'Yes, share' : 'No thanks'}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted">
            Ages {AGE_GATES.dataOptInAge}+. Measuring and reports work the same either way.{' '}
            <Link className="underline" to="/legal/detection-improvement">
              Detection-improvement data notice
            </Link>
          </p>
        </Card>
      ) : null}

      <Card className="space-y-4">
        <Toggle
          checked={acceptTerms}
          onChange={setAcceptTerms}
          label="I agree to the Terms of Service and Privacy Policy"
          description="Version dated 2026-09-24."
        />
        <Toggle
          checked={acceptHealthData}
          onChange={setAcceptHealthData}
          label="Store my session summaries in my account"
          description="Posture angles and scores per session. Separate from the terms above, and you can delete them at any time."
        />
        <p className="text-xs text-muted">
          Your video never leaves this device. Only the numbers above are stored.{' '}
          <Link className="underline" to="/legal/consumer-health-data">
            Consumer Health Data Privacy Policy
          </Link>
        </p>
      </Card>

      <Button disabled={!ready || busy} onClick={() => void submit()} className="w-full">
        {busy ? 'Saving…' : 'Continue'}
      </Button>
      <p className="text-xs text-muted">{DISCLAIMERS.long}</p>
    </div>
  );
}
